import { useCallback, useEffect, useState } from 'react'
import { listLicenses, listOrganisations } from '../../services/admin'
import {
  listEntitlements,
  listLicenseCriteria,
  listXeroInvoiceStates,
} from '../../services/licensing'
import type {
  Entitlement,
  License,
  LicenseCriteria,
  Organisation,
  XeroInvoiceState,
} from '../../types'

const DEFAULT_LIMIT = 100

export interface LicenseManagementData {
  licenses: License[]
  organisations: Organisation[]
  criteria: LicenseCriteria[]
  entitlements: Entitlement[]
  xeroInvoices: XeroInvoiceState[]
  xeroStates: Map<string, XeroInvoiceState>
  loading: boolean
  error: string | null
  reload: () => Promise<void>
}

function buildXeroStateMap(states: XeroInvoiceState[]): Map<string, XeroInvoiceState> {
  const stateMap = new Map<string, XeroInvoiceState>()
  for (const state of states) {
    if (state.entitlementId) stateMap.set(state.entitlementId, state)
  }
  return stateMap
}

export function useLicenseManagementData(): LicenseManagementData {
  const [licenses, setLicenses] = useState<License[]>([])
  const [organisations, setOrganisations] = useState<Organisation[]>([])
  const [criteria, setCriteria] = useState<LicenseCriteria[]>([])
  const [entitlements, setEntitlements] = useState<Entitlement[]>([])
  const [xeroInvoices, setXeroInvoices] = useState<XeroInvoiceState[]>([])
  const [xeroStates, setXeroStates] = useState<Map<string, XeroInvoiceState>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const [licensesRes, orgsRes, criteriaRes, entitlementsRes, xeroRes] = await Promise.all([
        listLicenses({ limit: DEFAULT_LIMIT }),
        listOrganisations({ limit: DEFAULT_LIMIT }),
        listLicenseCriteria({ limit: DEFAULT_LIMIT }),
        listEntitlements({ limit: DEFAULT_LIMIT }),
        listXeroInvoiceStates({ limit: DEFAULT_LIMIT }),
      ])
      setLicenses(licensesRes.data)
      setOrganisations(orgsRes.data)
      setCriteria(criteriaRes.data)
      setEntitlements(entitlementsRes.data)
      setXeroInvoices(xeroRes.data)
      setXeroStates(buildXeroStateMap(xeroRes.data))
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  return {
    licenses,
    organisations,
    criteria,
    entitlements,
    xeroInvoices,
    xeroStates,
    loading,
    error,
    reload,
  }
}
